import { type AnyAction, combineReducers } from 'redux';
import Immutable from 'immutable';

import type {
    AssetContainerModel,
    FavoritedAsset,
    GenerativeMediaModels,
    SlideBackgroundModel,
    SortEnum,
    SuggestedMediaEntryResponse,
    TaskResultStatusEnum,
    TransitionedAssetError,
} from 'js/src/client';
import { AssetContainerTypes, AssetTypes, SlideBackgroundModelTypeEnum } from 'js/src/client';
import type { UploadedFile } from 'js/src/components/sketch/FileUpload';
import { switchEditorTabAction } from 'js/src/data/app/app-actions';
import {
    addMediaToUploads,
    addNewFolder,
    addPreviousMediaGenerations,
    addRecentlyUploadedAssetToAFolder,
    changeActiveSearchContext,
    changeCorrectedMediaSearchTerm,
    changeMediaSearchTerm,
    changeRelatedMediaSearchTerms,
    changeSuggestedMediaSearchTerm,
    clearMediaErrors,
    deleteAudioSuccess,
    deleteFolder,
    deleteMediaResult,
    editFolderName,
    moveAssetToFolder,
    receiveDamConnectionError,
    receiveMediaError,
    receiveMediaResponse,
    resetFolderSearchState,
    setActiveAIMediaSubTab,
    setActiveFolder,
    setAIMediaModels,
    setAISimilarGenerations,
    setAISimilarGenerationsLoading,
    setAllFavorites,
    setAudioDurationFilter,
    setCarouselMediaResults,
    setChartGenerationAsset,
    setCurrentAudioPlayingStatus,
    setCurrentMediaGenerations,
    setCurrentMediaQueryScene,
    setCurrentPlayingAudioDuration,
    setCurrentPlayingAudioScrubberSeekPercent,
    setCurrentPlayingAudioSeekPercent,
    setCurrentPlayingAudioUrl,
    setFilter,
    setIsFetchingRecommendedSearchTerms,
    setIsInitializingImageGeneration,
    setIsInitializingVideoGeneration,
    setIsMediaQuerySearchLoading,
    setIsSimilarSearchContext,
    setIsSimilarSearchLoading,
    setMediaErrors,
    setMediaQuerySearchResults,
    setPreviousMediaGenerations,
    setRecommendedSearchTerms,
    setSavingAIMedia,
    setSelectedMediaLicenseFilter,
    setSelectedMediaTypeFilter,
    setSelectedReferenceImage,
    setSimilarSearchOriginalAsset,
    setSimilarSearchResults,
    setSort,
    setUserSelectedGenMediaAspectRatio,
    setVideoGenerationTask,
    showMediaQuerySuggestionError,
    startMediaSearch,
    storeAllFolders,
    storeFrequentlyUsedTags,
    toggleFavorite,
    toggleFilterByUserLevel,
    toggleMediaSourceFilter,
    updateFolderThumbnail,
    updateMediaAttribution,
    updateMediaTags,
} from 'js/src/data/media/media-actions';
import type {
    AudioDurationRange,
    MEDIA_SOURCE_OPTIONS,
    MediaFolderWithContents,
    ModifiedAssetGenerationResponse,
    SearchContextValueType,
} from 'js/src/data/media/media-types';
import {
    AI_MEDIA_SUB_TAB_OPTIONS,
    DEFAULT_SORT_METHOD,
    EDITOR_TAB_TO_SEARCH_CONTEXT_MAPPING,
    FILTER_BY_LEVELS,
    immutableSlideBackgroundModelIsImmutableImageBackgroundModel,
    immutableSlideBackgroundModelIsImmutableVideoBackgroundModel,
    isMediaSearchResponse,
    MEDIA_LICENSE_OPTIONS,
    MEDIA_STORE_PREFIX,
    MEDIA_TYPE_OPTIONS,
    SEARCH_CONTEXT,
    SEARCH_CONTEXT_TO_EDITOR_TAB_MAPPING,
    slideBackgroundModelIsImageBackgroundModel,
    slideBackgroundModelIsVideoBackgroundModel,
    UPLOADS_FOLDER_ORM_PREFIX,
} from 'js/src/data/media/media-types';
import {
    checkAssetUUID,
    resetUploadsFolderStore,
    setUploadsFolderStore,
} from 'js/src/data/media/media-utils';
import StoreORM, { ID_FIELD, type StoreORMInterface } from 'js/src/data/store-orm';
import {
    assetContainerModelIsAudio,
    assetContainerModelIsBackground,
    immutableAssetContainerModelIsBackground,
    immutableAssetContainerModelIsIcon,
} from 'js/src/data/uploads';
import { removeUpload } from 'js/src/data/uploads/uploads-actions';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';

/*
    Data stored for each search context
*/

export function media_results(
    state = StoreORM.newFromIdPrefix<AssetContainerModel, string>(MEDIA_STORE_PREFIX).data,
    action: AnyAction,
) {
    if (startMediaSearch.match(action)) {
        if (!action.payload.append_results) {
            // clear the data if we are starting a new search
            return StoreORM.newFromIdPrefix<AssetContainerModel, string>(MEDIA_STORE_PREFIX).data;
        }
        return state;
    }
    if (receiveMediaResponse.match(action)) {
        let orm: StoreORM<AssetContainerModel, string>;
        if (action.payload.append_results) {
            orm = new StoreORM<AssetContainerModel, string>(state);
        } else {
            // make an entirely new store to overwrite the past results
            orm = StoreORM.newFromIdPrefix<AssetContainerModel, string>(MEDIA_STORE_PREFIX);
        }

        // push any premium results into the orm
        const new_premium_results = isMediaSearchResponse(action.payload.response)
            ? action.payload.response.premium_results
            : [];
        const premium_results_to_interleave: Record<number, AssetContainerModel> = {}; // dict of form {index_to_append: value}
        if (new_premium_results) {
            /*
            There are premium assets which we want to INTERLEAVE with the other results.  We want it to be
            deterministic but also somewhat random.  How can we do this? By using the result's UUIDs!
            What we'll do is divide the number of results into buckets, one for each premium result.  Then
            we'll determine where to add the premium result based on it's UUID (specifically the first char).
             */
            // for now, cap the number of premium results per page to 2 - once finalized, remove this
            const num_premium_results = Math.min(new_premium_results.length, 2);

            // determine the buckets
            const bucket_size = Math.floor(
                action.payload.response.results.length / num_premium_results,
            );

            for (let i = 0; i < num_premium_results; i++) {
                // determine the relative position in the bucket based on the hex value of the first char of uuid
                const result = new_premium_results[i];
                if (assetContainerModelIsAudio(result)) {
                    throw Error(
                        'There should not be premium results for audio assets - unsupported currently!',
                    );
                }
                if (!assetContainerModelIsBackground(result)) {
                    throw Error('only background assets are supported for premium results');
                }
                const background = result[result['type']];
                const asset = background[background['type']];
                if (!asset) {
                    throw Error('asset not found in premium result');
                }

                const uuid_first_char = checkAssetUUID(asset['uuid'])[0];
                const uuid_to_number = parseInt(`0x${uuid_first_char}`);
                const offset = Math.floor((uuid_to_number * bucket_size) / 15);
                premium_results_to_interleave[i * bucket_size + offset] = result;
            }
        }

        // push all of the results into the orm
        for (let i = 0; i < action.payload.response.results.length; i++) {
            // if there's a premium result to interleave, interleave it! :D
            if (premium_results_to_interleave[i]) {
                orm.pushJSItem(premium_results_to_interleave[i]);
            }

            orm.pushJSItem(action.payload.response.results[i]);
        }

        // handle case where no normal results but there is a premium result
        if (action.payload.response.results.length === 0) {
            // if there's a premium result to interleave, interleave it! :D
            if (premium_results_to_interleave[0]) {
                orm.pushJSItem(premium_results_to_interleave[0]);
            }
        }

        return orm.data;
    }
    if (moveAssetToFolder.match(action) || removeUpload.match(action)) {
        const orm = new StoreORM<AssetContainerModel, string>(state);
        if (immutableAssetContainerModelIsBackground(action.payload.asset)) {
            const assetType = AssetContainerTypes.BACKGROUND;
            const background = action.payload.asset.get(assetType);
            const slideBackgroundType = background.get('type');
            let uuid;
            if (immutableSlideBackgroundModelIsImmutableImageBackgroundModel(background)) {
                uuid = background.get(SlideBackgroundModelTypeEnum.IMAGE).get('uuid');
            } else if (immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(background)) {
                uuid = background.get(SlideBackgroundModelTypeEnum.VIDEO).get('uuid');
            } else {
                throw new Error('unknown slide background type');
            }
            const ids = orm.queryIds({
                key: [assetType, slideBackgroundType, 'uuid'],
                value: uuid,
            });

            if (ids.size === 1) {
                orm.delete(ids);
                return orm.data;
            }
        } else if (immutableAssetContainerModelIsIcon(action.payload.asset)) {
            const asset_type = action.payload.asset.get('type');
            const asset_uuid = action.payload.asset.get(asset_type).get('uuid');
            const ids = orm.queryIds({
                key: [asset_type, 'uuid'],
                value: asset_uuid,
            });

            if (ids.size === 1) {
                orm.delete(ids);
                return orm.data;
            }
        }
        return state;
    }
    if (deleteMediaResult.match(action)) {
        const orm = new StoreORM<AssetContainerModel, string>(state);
        const { mediaUuid, assetType, slideBackgroundType } = action.payload;

        const ids = orm.queryIds({
            key: [assetType, slideBackgroundType, 'uuid'],
            value: mediaUuid,
        });
        orm.delete(ids);
        return orm.data;
    }
    if (updateMediaTags.match(action)) {
        const orm = new StoreORM<AssetContainerModel, string>(state);
        let keyPath = [action.payload.mediaType, 'tags'];
        if (
            action.payload.mediaType === AssetTypes.IMAGE ||
            action.payload.mediaType === AssetTypes.VIDEO
        ) {
            keyPath = [AssetContainerTypes.BACKGROUND, action.payload.mediaType, 'tags'];
        }
        orm.updateById(action.payload.mediaORMId, {
            key: keyPath,
            value: action.payload.mediaTags,
        });
        return orm.data;
    }
    if (updateMediaAttribution.match(action)) {
        const orm = new StoreORM<AssetContainerModel, string>(state);
        let keyPath = [action.payload.mediaType, 'attribution'];
        if (
            action.payload.mediaType === AssetTypes.IMAGE ||
            action.payload.mediaType === AssetTypes.VIDEO
        ) {
            keyPath = [AssetContainerTypes.BACKGROUND, action.payload.mediaType, 'attribution'];
        }
        orm.updateById(action.payload.mediaORMId, {
            key: keyPath,
            value: action.payload.mediaAttribution,
        });
        return orm.data;
    }
    if (addMediaToUploads.match(action)) {
        // when we have a new upload, store this in media_results as well, but tag it as current_session_upload
        // so we can prioritize it in our UI
        const orm = new StoreORM<AssetContainerModel, string>(state);
        const asset = action.payload.mediaAsset;
        if (!asset) {
            return orm.data;
        }
        const mediaResult = {
            ...asset,
            current_session_upload: true,
        };
        orm.pushJSItemToFront(mediaResult);
        return orm.data;
    }
    if (deleteAudioSuccess.match(action)) {
        const orm = new StoreORM<AssetContainerModel, string>(state);
        const orm_ids = orm.queryIds({
            key: ['audio', 'id'],
            value: action.payload.id,
        });
        orm.delete(orm_ids);
        return orm.data;
    }
    return state;
}

export function no_results(state = false, action: AnyAction) {
    if (startMediaSearch.match(action)) {
        if (!action.payload.append_results) {
            // if we are starting a new search, we shouldn't
            // assume there are no results.
            return false;
        }
        return state;
    }
    if (receiveMediaResponse.match(action)) {
        return (
            action.payload.response.results.length === 0 &&
            action.payload.num_existing_results === 0
        );
    }
    return state;
}

export function no_access(state = false, action: AnyAction) {
    if (receiveMediaResponse.match(action)) {
        return action.payload.response.error === 'no_access';
    }
    if (receiveDamConnectionError.match(action)) {
        return true;
    }
    return state;
}

export function more_results(state = true, action: AnyAction) {
    if (receiveMediaResponse.match(action)) {
        return action.payload.response.next !== null;
    }
    if (receiveMediaError.match(action)) {
        return false;
    }
    return state;
}

export function page(state = 1, action: AnyAction) {
    if (startMediaSearch.match(action)) {
        if (action.payload.append_results === false) {
            return 1;
        }
        return state;
    }
    if (receiveMediaResponse.match(action)) {
        return action.payload.page + 1;
    }
    return state;
}

/**
 * Only used for TENOR search
 */
export function pos(state: null | string = null, action: AnyAction) {
    if (startMediaSearch.match(action)) {
        if (action.payload.append_results === false) {
            return null;
        }
        return state;
    }
    if (receiveMediaResponse.match(action)) {
        return action.payload.pos;
    }
    return state;
}

/**
 * Used for DAM search
 */
export function next(state: null | string = null, action: AnyAction) {
    if (startMediaSearch.match(action)) {
        if (action.payload.append_results === false) {
            return null;
        }
        return state;
    }
    if (receiveMediaResponse.match(action)) {
        return action.payload.response.next;
    }
    return state;
}

export function last_searched_term(state = '', action: AnyAction) {
    if (startMediaSearch.match(action)) {
        return action.payload.search_term;
    }
    return state;
}

export function last_used_filters(state: string[] = [], action: AnyAction) {
    if (startMediaSearch.match(action)) {
        return action.payload.filter;
    }
    return state;
}

export function search_term(state = '', action: AnyAction) {
    if (changeMediaSearchTerm.match(action)) {
        return action.payload.search_term;
    }
    return state;
}

export function corrected_search_term(state = '', action: AnyAction) {
    if (changeCorrectedMediaSearchTerm.match(action)) {
        return action.payload.search_term;
    }
    if (startMediaSearch.match(action)) {
        return action.payload.page === 1 ? '' : state;
    }
    return state;
}

export function corrected_search_term_indices(
    state: ImmutableMap<[number, number]> | null = null,
    action: AnyAction,
) {
    if (changeCorrectedMediaSearchTerm.match(action)) {
        return Immutable.List(action.payload.search_term_indices);
    }
    return state;
}

export function suggested_search_term(state = '', action: AnyAction) {
    if (startMediaSearch.match(action)) {
        return action.payload.page === 1 ? '' : state;
    }
    if (changeSuggestedMediaSearchTerm.match(action)) {
        return action.payload.query;
    }
    return state;
}

export function highlighted_suggested_search_term(state = '', action: AnyAction) {
    if (changeSuggestedMediaSearchTerm.match(action)) {
        return action.payload.highlighted_query;
    }
    return state;
}

export function related_search_terms(state = Immutable.List<string>(), action: AnyAction) {
    if (changeRelatedMediaSearchTerms.match(action)) {
        return Immutable.List(action.payload.popular_queries);
    }
    return state;
}

export function frequently_used_tags(
    state = Immutable.List<string>(),
    action: AnyAction,
): Immutable.List<string> {
    if (storeFrequentlyUsedTags.match(action)) {
        return action.payload.frequently_used_tags;
    }
    return state;
}

export function sourceFilter(
    state = Immutable.Map() as ImmutableMap<Partial<Record<MEDIA_SOURCE_OPTIONS, boolean>>>,
    action: AnyAction,
) {
    /*
    The filter is a map of the different ways that the media results
    can be filtered. In order for a type of media to be filtered out,
    the property must exist in the state AND be set to false. For example,
    this would filter out premium media, but leave basic media:
    {
        premium: false
        // no 'basic' property, so defaults to "true"
    }

    This is used to power the filter for media search, specifically the "source" checkboxes:
    - [ ] Basic
    - [ ] Premium
    - [ ] Marketplace
    */
    if (setFilter.match(action)) {
        return action.payload.filter;
    }
    if (toggleMediaSourceFilter.match(action)) {
        return state.set(action.payload.filter_key, action.payload.filter_value);
    }
    return state;
}

export function selectedType(state = MEDIA_TYPE_OPTIONS.ALL, action: AnyAction) {
    /*
    This is used to power the filter for media search, specifically the "type" radios:
    - ( ) All
    - ( ) Videos
    - ( ) Images
     */
    if (setSelectedMediaTypeFilter.match(action)) {
        return action.payload.selected_type;
    }
    return state;
}

export function audioDurationFilter(
    state: AudioDurationRange = { min: 0, max: null },
    action: AnyAction,
): AudioDurationRange {
    /*
    This is used to power the duration filter for audio search:
    - min: minimum duration in seconds
    - max: maximum duration in seconds (null for no max)
     */
    if (setAudioDurationFilter.match(action)) {
        return action.payload.duration_range;
    }
    return state;
}

export function selectedLicense(state = MEDIA_LICENSE_OPTIONS.ALL, action: AnyAction) {
    /*
    This is used to power the filter for media search, specifically the "license type" radios:
    - ( ) All
    - ( ) Stock
    - ( ) Editorial
     */
    if (setSelectedMediaLicenseFilter.match(action)) {
        return action.payload.selected_license_type;
    }
    return state;
}

export function filter_by_user_level(
    state = FILTER_BY_LEVELS.filter_by_workspace_level,
    action: AnyAction,
) {
    if (toggleFilterByUserLevel.match(action)) {
        return action.payload.filter_by_user_level;
    }
    return state;
}

export function sort(state: SortEnum = DEFAULT_SORT_METHOD, action: AnyAction) {
    if (setSort.match(action)) {
        return action.payload.sort_method;
    }
    return state;
}

export function isSearchInProgress(state = false, action: AnyAction) {
    if (setActiveFolder.match(action)) {
        if (!action.payload.folderId) {
            return false;
        }
        return state;
    }
    if (startMediaSearch.match(action)) {
        return true;
    }
    if (receiveMediaResponse.match(action) || receiveMediaError.match(action)) {
        if (!action.payload.folderId) {
            return false;
        }
        return state;
    }
    return state;
}
/*
    End data stored for each search context
*/
export function search_context(
    state = ImmutableFromJS(EDITOR_TAB_TO_SEARCH_CONTEXT_MAPPING),
    action: AnyAction,
) {
    if (changeActiveSearchContext.match(action)) {
        const searchContext = action.payload.search_context;
        const editorTab = SEARCH_CONTEXT_TO_EDITOR_TAB_MAPPING[searchContext];
        return state.set(editorTab, searchContext);
    }
    return state;
}

export function current_media_query_scene(state: string | null = null, action: AnyAction) {
    if (setCurrentMediaQueryScene.match(action)) {
        return action.payload.sceneId;
    }
    return state;
}

export function is_media_query_search_loading(state = false, action: AnyAction) {
    if (setIsMediaQuerySearchLoading.match(action)) {
        return action.payload.isLoading;
    }
    return state;
}

type MediaQuerySearchResults = Immutable.List<
    ImmutableMap<{
        query: string;
        results: AssetContainerModel[];
    }>
>;
export function media_query_search_results(
    state: MediaQuerySearchResults = Immutable.List(),
    action: AnyAction,
): MediaQuerySearchResults {
    if (setMediaQuerySearchResults.match(action)) {
        // The backend returns SlideBackgroundModels, but we want to store them as AssetContainerModels
        // so we can have compatibility with the other media tabs.
        // In the future, we can refactor the backend to return AssetContainerModels directly.
        const querySamples = action.payload.searchResults.map(
            (querySample: SuggestedMediaEntryResponse) => {
                const assetContainers: AssetContainerModel[] = querySample.results.map(
                    (background: SlideBackgroundModel) => {
                        return {
                            type: AssetContainerTypes.BACKGROUND,
                            background,
                            audio: null,
                            icon: null,
                            font: null,
                            document: null,
                            folder_id: null,
                            article_id: null,
                            document_id: null,
                        };
                    },
                );
                return {
                    ...querySample,
                    results: assetContainers,
                };
            },
        );
        return ImmutableFromJS(querySamples);
    }
    return state;
}

export function show_media_query_suggestion_error(state = false, action: AnyAction) {
    if (showMediaQuerySuggestionError.match(action)) {
        return action.payload.show;
    }
    return state;
}

export function is_similar_search_context(state = false, action: AnyAction): boolean {
    if (setIsSimilarSearchContext.match(action)) {
        return action.payload;
    }
    return state;
}

export function is_similar_search_loading(state = false, action: AnyAction): boolean {
    if (setIsSimilarSearchLoading.match(action)) {
        return action.payload;
    }
    return state;
}

export function similar_search_results(
    state = Immutable.List<ImmutableMap<AssetContainerModel>>(),
    action: AnyAction,
): Immutable.List<ImmutableMap<AssetContainerModel>> {
    if (setSimilarSearchResults.match(action)) {
        return action.payload.results;
    }
    return state;
}

export function similar_search_original_asset(
    state: ImmutableMap<AssetContainerModel> | null = null,
    action: AnyAction,
): ImmutableMap<AssetContainerModel> | null {
    if (setSimilarSearchOriginalAsset.match(action)) {
        return action.payload.originalAsset;
    }
    return state;
}

export function ai_similar_generations_loading(state = false, action: AnyAction): boolean {
    if (setAISimilarGenerationsLoading.match(action)) {
        return action.payload;
    }
    return state;
}

export function ai_similar_generations(
    state: ModifiedAssetGenerationResponse[] = [],
    action: AnyAction,
): ModifiedAssetGenerationResponse[] {
    if (setAISimilarGenerations.match(action)) {
        return action.payload.append
            ? [...state, ...action.payload.results]
            : action.payload.results;
    }
    return state;
}

export function selected_reference_image(
    state: UploadedFile | null = null,
    action: AnyAction,
): UploadedFile | null {
    if (setSelectedReferenceImage.match(action)) {
        return action.payload;
    }
    return state;
}

export function active_ai_media_sub_tab(state = AI_MEDIA_SUB_TAB_OPTIONS.IMAGE, action: AnyAction) {
    if (setActiveAIMediaSubTab.match(action)) {
        return action.payload;
    }
    return state;
}

function updateAIMediaGenerationSavingState(
    state: Array<ModifiedAssetGenerationResponse>,
    payload: { generationUuid: string; isSaving: boolean },
): Array<ModifiedAssetGenerationResponse> {
    const { generationUuid, isSaving } = payload;
    return state.map((generation) => {
        if (generation?.result?.uuid === generationUuid) {
            return {
                ...generation,
                result: {
                    ...generation.result,
                    isSaving,
                },
            };
        }
        return generation;
    });
}

export function current_media_generations(
    state: Array<ModifiedAssetGenerationResponse> = [],
    action: AnyAction,
): Array<ModifiedAssetGenerationResponse> {
    if (setCurrentMediaGenerations.match(action)) {
        return action.payload;
    }
    if (setSavingAIMedia.match(action)) {
        const s = updateAIMediaGenerationSavingState(state, action.payload);
        return s;
    }
    return state;
}

export function previous_media_generations(
    state: Array<ModifiedAssetGenerationResponse> = [],
    action: AnyAction,
): Array<ModifiedAssetGenerationResponse> {
    if (setPreviousMediaGenerations.match(action)) {
        return action.payload;
    }
    if (addPreviousMediaGenerations.match(action)) {
        // Reverse the previous media generations so that the most recent generations are shown first
        const reversedMediaGenerations = [...action.payload].reverse();
        return [...reversedMediaGenerations, ...state];
    }
    if (setSavingAIMedia.match(action)) {
        return updateAIMediaGenerationSavingState(state, action.payload);
    }
    return state;
}

export function is_fetching_recommended_search_terms(state = false, action: AnyAction) {
    if (setIsFetchingRecommendedSearchTerms.match(action)) {
        return action.payload;
    }
    return state;
}

export function recommended_search_terms(state = Immutable.List<string>(), action: AnyAction) {
    if (setRecommendedSearchTerms.match(action)) {
        return action.payload.terms;
    }
    return state;
}

export function all_favorite_media(
    state = StoreORM.newFromIdPrefix<FavoritedAsset, string>('user-favourite-media').data,
    action: AnyAction,
) {
    if (setAllFavorites.match(action)) {
        const orm = new StoreORM<FavoritedAsset, string>(state);
        // push all of the results into the orm
        for (let i = 0; i < action.payload.allFavoriteMedia.length; i++) {
            orm.pushJSItem(action.payload.allFavoriteMedia[i], 'uuid');
        }
        return orm.data;
    }
    if (toggleFavorite.match(action)) {
        const { favorite, ...favoritedAsset } = action.payload;
        const orm = new StoreORM<FavoritedAsset, string>(state);
        if (favorite) {
            orm.pushJSItem(favoritedAsset, 'uuid');
        } else {
            orm.delete([favoritedAsset.uuid]);
        }
        return orm.data;
    }
    return state;
}

const initialAudioDetails = ImmutableFromJS({
    url: null as string | null,
    seekPercent: 0,
    scrubberSeekPercent: 0,
    duration: 0,
    isPlaying: false,
});

export function playingAudioDetails(state = initialAudioDetails, action: AnyAction) {
    if (switchEditorTabAction.match(action)) {
        // stop any playing audio when switching editor tabs
        return state.set('isPlaying', false);
    }
    if (setCurrentPlayingAudioUrl.match(action)) {
        return state.set('url', action.payload.audioUrl);
    }
    if (setCurrentPlayingAudioSeekPercent.match(action)) {
        return state.set('seekPercent', action.payload.seekPercent);
    }
    if (setCurrentPlayingAudioScrubberSeekPercent.match(action)) {
        return state.set('scrubberSeekPercent', action.payload.scrubberSeekPercent);
    }
    if (setCurrentPlayingAudioDuration.match(action)) {
        return state.set('duration', action.payload.duration);
    }
    if (setCurrentAudioPlayingStatus.match(action)) {
        return state.set('isPlaying', action.payload.isPlaying);
    }
    return state;
}

const INITIAL_ACTIVE_UPLOADS_FOLDER = Immutable.Map<SearchContextValueType, number | null>({
    [SEARCH_CONTEXT.UPLOADED_VISUALS]: null,
    [SEARCH_CONTEXT.UPLOADED_ELEMENTS]: null,
});
export function active_uploads_folder(state = INITIAL_ACTIVE_UPLOADS_FOLDER, action: AnyAction) {
    if (setActiveFolder.match(action)) {
        if (
            action.payload.searchContext !== SEARCH_CONTEXT.UPLOADED_VISUALS &&
            action.payload.searchContext !== SEARCH_CONTEXT.UPLOADED_ELEMENTS
        ) {
            throw Error(
                `You cannot set the active folder for a search context that does not support folders. Got ${action.payload.searchContext}`,
            );
        }
        return state.set(action.payload.searchContext, action.payload.folderId);
    }
    return state;
}

export function all_uploads_folders(
    state = StoreORM.newFromIdPrefix<MediaFolderWithContents>(UPLOADS_FOLDER_ORM_PREFIX).data,
    action: AnyAction,
) {
    if (storeAllFolders.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        action.payload.folders.forEach((folder) => {
            orm.pushItem(folder, 'id');
        });
        return orm.data;
    }
    if (addNewFolder.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        const folder = setUploadsFolderStore(action.payload.folder);
        orm.pushItemToFront(folder, 'id');
        return orm.data;
    }
    if (editFolderName.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        const folderId = action.payload.folder.get('id');
        const existingFolder = orm.getById(folderId);
        const updatedFolder = existingFolder.merge(action.payload.folder);
        orm.updateById(folderId, { value: updatedFolder, key: [] });
        return orm.data;
    }
    if (deleteFolder.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        orm.delete([action.payload.folderId]);
        return orm.data;
    }
    if (addRecentlyUploadedAssetToAFolder.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        const { folderId, mediaAsset } = action.payload;
        const folderDetails = orm.getById(folderId);
        let thumbnailUrl = '';
        if (assetContainerModelIsBackground(mediaAsset)) {
            // we can only have preview for a image or video. Icons will use default folder preview
            const background = mediaAsset.background;
            if (slideBackgroundModelIsImageBackgroundModel(background)) {
                thumbnailUrl = background.image.thumbnail_url;
            } else if (slideBackgroundModelIsVideoBackgroundModel(background)) {
                thumbnailUrl = background.video.poster_url;
            }
        }

        if (folderDetails) {
            // 1. Update the media results
            const mediaStoreOrm = new StoreORM<AssetContainerModel, string>(
                folderDetails.get('media_results'),
            );
            const mediaResult = {
                ...action.payload.mediaAsset,
                current_session_upload: true,
            };
            mediaStoreOrm.pushJSItem(mediaResult);

            // update the total count
            const mediaCount = folderDetails.get('media_count');

            orm.updateById(action.payload.folderId, {
                key: ['media_results'],
                value: mediaStoreOrm.data,
            });

            orm.updateById(action.payload.folderId, {
                key: ['media_count'],
                value: mediaCount + 1,
            });

            orm.updateById(action.payload.folderId, {
                key: ['thumbnail_url'],
                value: thumbnailUrl,
            });
        }
        return orm.data;
    }
    if (removeUpload.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        if (action.payload.activeFolderId) {
            const assetId = action.payload.asset.get(ID_FIELD as never); // typescipt hack to access the _id field, which is not in the type. We should stop using client-generated IDs
            const folderMediaResults = orm
                .getById(action.payload.activeFolderId)
                .get('media_results');
            const mediaCount = orm.getById(action.payload.activeFolderId).get('media_count');
            const mediaStoreOrm = new StoreORM(folderMediaResults);

            mediaStoreOrm.delete([assetId]);
            orm.updateById(action.payload.activeFolderId, {
                key: ['media_results'],
                value: mediaStoreOrm.data,
            });

            orm.updateById(action.payload.activeFolderId, {
                key: ['media_count'],
                value: mediaCount - 1,
            });

            // if we happen to delete the asset that is thumbnail, set the icon to null
            // todo: could change this to call an endpoint to refresh the folder, but for now too expensive
            const asset = action.payload.asset;
            let thumbnailUrl = '';
            if (immutableAssetContainerModelIsBackground(asset)) {
                const background = asset.get(AssetContainerTypes.BACKGROUND);
                if (immutableSlideBackgroundModelIsImmutableImageBackgroundModel(background)) {
                    thumbnailUrl = background
                        .get(SlideBackgroundModelTypeEnum.IMAGE)
                        .get('thumbnail_url');
                } else if (
                    immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(background)
                ) {
                    thumbnailUrl = background
                        .get(SlideBackgroundModelTypeEnum.VIDEO)
                        .get('poster_url');
                }
            }
            const folderDetails = orm.getById(action.payload.activeFolderId);
            if (thumbnailUrl === folderDetails.get('thumbnail_url')) {
                orm.updateById(action.payload.activeFolderId, {
                    key: ['thumbnail_url'],
                    value: undefined,
                });
            }
        }
        return orm.data;
    }
    if (moveAssetToFolder.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        const assetId = action.payload.asset.get(ID_FIELD as never); // typescipt hack to access the _id field, which is not in the type. We should stop using client-generated IDs
        const targetFolderId = action.payload.targetFolderId;
        // If we're moving from inside a folder to another folder, remove it from there.
        if (action.payload.sourceFolderId && action.payload.sourceFolderId !== targetFolderId) {
            const folderMediaResults = orm
                .getById(action.payload.sourceFolderId)
                .get('media_results');
            const sourceMediaCount = orm.getById(action.payload.sourceFolderId).get('media_count');
            const mediaStoreOrm = new StoreORM(folderMediaResults);
            mediaStoreOrm.delete([assetId]);
            orm.updateById(action.payload.sourceFolderId, {
                key: ['media_results'],
                value: mediaStoreOrm.data,
            });

            orm.updateById(action.payload.sourceFolderId, {
                key: ['media_count'],
                value: sourceMediaCount - 1,
            });
        }
        const targetFolderMediaResults = orm.getById(targetFolderId).get('media_results');
        const targetMediaCount = orm.getById(targetFolderId).get('media_count');
        const targetMediaStoreOrm = new StoreORM(targetFolderMediaResults);
        targetMediaStoreOrm.pushItem(action.payload.asset);

        orm.updateById(targetFolderId, {
            key: ['media_results'],
            value: targetMediaStoreOrm.data,
        });

        orm.updateById(targetFolderId, {
            key: ['media_count'],
            value: targetMediaCount + 1,
        });

        return orm.data;
    }
    if (updateMediaTags.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        const folderId = action.payload.folderId;
        if (!folderId) {
            // no folderId (set media tags for non-folders)
            // this is expected - fail silently
            return state;
        }
        const folderDetails = orm.getById(folderId);
        if (!folderDetails) {
            // either no folderId (set sort for non-folders)
            // or folder doesn't exist, either way this is expected error case, fail silently
            return state;
        }
        const mediaStoreOrm = new StoreORM(folderDetails.get('media_results'));
        let keyPath = [action.payload.mediaType, 'tags'];
        if (
            action.payload.mediaType === AssetTypes.IMAGE ||
            action.payload.mediaType === AssetTypes.VIDEO
        ) {
            keyPath = [AssetContainerTypes.BACKGROUND, action.payload.mediaType, 'tags'];
        }
        mediaStoreOrm.updateById(action.payload.mediaORMId, {
            key: keyPath,
            value: action.payload.mediaTags,
        });

        orm.updateById(folderId, {
            key: ['media_results'],
            value: mediaStoreOrm.data,
        });

        return orm.data;
    }
    if (updateMediaAttribution.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        const folderId = action.payload.folderId;
        if (!folderId) {
            return state;
        }
        const folderDetails = orm.getById(folderId);
        if (!folderDetails) {
            return state;
        }
        const mediaStoreOrm = new StoreORM(folderDetails.get('media_results'));
        let keyPath = [action.payload.mediaType, 'attribution'];
        if (
            action.payload.mediaType === AssetTypes.IMAGE ||
            action.payload.mediaType === AssetTypes.VIDEO
        ) {
            keyPath = [AssetContainerTypes.BACKGROUND, action.payload.mediaType, 'attribution'];
        }
        mediaStoreOrm.updateById(action.payload.mediaORMId, {
            key: keyPath,
            value: action.payload.mediaAttribution,
        });

        orm.updateById(folderId, {
            key: ['media_results'],
            value: mediaStoreOrm.data,
        });

        return orm.data;
    }
    if (setSort.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        if (!action.payload.folderId) {
            return state;
        }
        const folder = orm.getById(action.payload.folderId);
        if (!folder) {
            // either no folderId (set sort for non-folders)
            // or folder doesn't exist, either way this is expected error case, fail silently
            return state;
        }
        // reset everything (including pageNo for search results so we get 1st page of results)
        resetUploadsFolderStore(orm, action.payload.folderId);
        // then set the sort method to the one that was clicked
        orm.updateById(action.payload.folderId, {
            key: ['sort'],
            value: action.payload.sort_method,
        });
        return orm.data;
    }
    if (toggleMediaSourceFilter.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        const folderId = action.payload.folderId;
        if (!folderId) {
            return state; // no folderId (set sort for non-folders), expected error case, fail silently
        }
        const folderDetails = orm.getByIdOrUndefined(folderId);
        if (!folderDetails) {
            return state; // folder doesn't exist, expected error case, fail silently
        }
        // reset everything (including pageNo for search results so we get 1st page of results)
        resetUploadsFolderStore(orm, folderId);

        let folderFilters = folderDetails.get('sourceFilter');
        folderFilters = folderFilters.set(action.payload.filter_key, action.payload.filter_value);

        orm.updateById(folderId, {
            key: ['sourceFilter'],
            value: folderFilters,
        });

        return orm.data;
    }
    if (setSelectedMediaTypeFilter.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        const folderId = action.payload.folderId;
        if (!folderId) {
            // no folderId (set media type filter for non-folders)
            // this is expected - fail silently
            return state;
        }
        const folderDetails = orm.getById(folderId);
        if (!folderDetails) {
            // folder doesn't exist, this is expected error case, fail silently
            return state;
        }
        // reset everything (including pageNo for search results so we get 1st page of results)
        resetUploadsFolderStore(orm, folderId);

        orm.updateById(folderId, {
            key: ['selectedType'],
            value: action.payload.selected_type,
        });
        return orm.data;
    }
    if (resetFolderSearchState.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        resetUploadsFolderStore(orm, action.payload.folder_id);
        return orm.data;
    }
    if (startMediaSearch.match(action)) {
        if (!action.payload.folderId) {
            return state;
        }
        const orm = new StoreORM<MediaFolderWithContents>(state);
        const folderId = action.payload.folderId;
        orm.updateById(folderId, {
            key: ['isSearchInProgress'],
            value: true,
        });
        return orm.data;
    }
    if (receiveMediaResponse.match(action)) {
        if (!action.payload.folderId) {
            return state;
        }

        // run through the media_results reducer to get the data
        // once we get the evaluated data, we just call folder.set() to set our reducers
        const orm = new StoreORM<MediaFolderWithContents>(state);
        const folderId = action.payload.folderId;
        const folderDetails = orm.getById(folderId);
        const reducerMap = media_reducers_map;
        let reducerName: keyof typeof media_reducers_map;
        for (reducerName in reducerMap) {
            const reducerValue = folderDetails.get(reducerName) as never;
            orm.updateById(folderId, {
                key: [reducerName],
                value: reducerMap[reducerName](reducerValue, action),
            });
        }

        // reset loading icon
        orm.updateById(folderId, {
            key: ['isSearchInProgress'],
            value: false,
        });

        return orm.data;
    }
    if (changeMediaSearchTerm.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        const folderId = action.payload.folderId;
        if (!folderId) {
            return state;
        }
        const folder = orm.getById(folderId);
        if (!folder) {
            return state;
        }
        const folderDetails = orm.getById(folderId);
        const reducedSearchTerm = search_term(folderDetails.get('search_term'), action);
        orm.updateById(folderId, {
            key: ['search_term'],
            value: reducedSearchTerm,
        });
        return orm.data;
    }
    if (updateFolderThumbnail.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        const { srcFolderId, srcThumbnailUrl, targetFolderId, targetThumbnailUrl } = action.payload;
        if (srcFolderId) {
            orm.updateById(srcFolderId, {
                key: ['thumbnail_url'],
                value: srcThumbnailUrl,
            });
        }
        if (targetFolderId) {
            orm.updateById(targetFolderId, {
                key: ['thumbnail_url'],
                value: targetThumbnailUrl,
            });
        }
        return orm.data;
    }
    if (storeFrequentlyUsedTags.match(action)) {
        const orm = new StoreORM<MediaFolderWithContents>(state);
        if (!action.payload.folderId) {
            return state;
        }

        orm.updateById(action.payload.folderId, {
            key: ['frequently_used_tags'],
            value: action.payload.frequently_used_tags,
        });
        return orm.data;
    }
    return state;
}

export function media_errors(
    state: TransitionedAssetError[] | null = null,
    action: AnyAction,
): TransitionedAssetError[] | null {
    if (setMediaErrors.match(action)) {
        return action.payload.errors;
    }
    if (clearMediaErrors.match(action)) {
        return null;
    }
    return state;
}

export function ai_media_models(
    state: GenerativeMediaModels[] = [],
    action: AnyAction,
): GenerativeMediaModels[] {
    if (setAIMediaModels.match(action)) {
        return action.payload;
    }
    return state;
}

export function video_generation_tasks(
    state: Record<string, { status: TaskResultStatusEnum; result?: SlideBackgroundModel }> = {},
    action: AnyAction,
): Record<string, { status: TaskResultStatusEnum; result?: SlideBackgroundModel }> {
    if (setVideoGenerationTask.match(action)) {
        const { taskId, ...taskData } = action.payload;
        return {
            ...state,
            [taskId]: taskData,
        };
    }
    return state;
}

export function chart_generation_asset(
    state: AssetContainerModel | null = null,
    action: AnyAction,
): AssetContainerModel | null {
    if (setChartGenerationAsset.match(action)) {
        return action.payload;
    }
    return state;
}

export function is_initializing_video_generation(state = false, action: AnyAction) {
    if (setIsInitializingVideoGeneration.match(action)) {
        return action.payload;
    }
    return state;
}

export function is_initializing_image_generation(state = false, action: AnyAction) {
    if (setIsInitializingImageGeneration.match(action)) {
        return action.payload;
    }
    return state;
}

export function carousel_media_results(
    state: Record<string, StoreORMInterface<AssetContainerModel, string>> = {},
    action: AnyAction,
): Record<string, StoreORMInterface<AssetContainerModel, string>> {
    if (setCarouselMediaResults.match(action)) {
        const { categoryId, results } = action.payload;
        const orm = StoreORM.newFromIdPrefix<AssetContainerModel, string>(MEDIA_STORE_PREFIX);
        results.forEach((result) => orm.pushJSItem(result));
        return {
            ...state,
            [categoryId]: orm.data,
        };
    }
    return state;
}

export function user_selected_gen_media_aspect_ratio(
    state: string | null = null,
    action: AnyAction,
): string | null {
    if (setUserSelectedGenMediaAspectRatio.match(action)) {
        return action.payload;
    }
    return state;
}

export const media_reducers_map = {
    media_results,
    no_access,
    no_results,
    more_results,
    page,
    pos,
    next,
    last_searched_term,
    last_used_filters,
    search_term,
    isSearchInProgress,
    sourceFilter,
    selectedType,
    audioDurationFilter,
    selectedLicense,
    filter_by_user_level,
    sort,
    corrected_search_term,
    corrected_search_term_indices,
    suggested_search_term,
    highlighted_suggested_search_term,
    related_search_terms,
    frequently_used_tags,
    carousel_media_results,
};

const reducer_override = (option: SearchContextValueType) => {
    const reducer = combineReducers(media_reducers_map);
    return (state: ReturnType<typeof reducer>, action: AnyAction) => {
        /*
        Override the normal reducer. Only send actions to the normal reducer
        that are either:
            - not media events
            - are media events that have a search_context property that matches
            the category that this reducer is responsible for
        */
        const sendToAllMediaReducers =
            action.sendToAllMediaReducers || action.payload?.sendToAllMediaReducers;
        if (sendToAllMediaReducers === true) {
            return reducer(state, action);
        }

        let sendToReducer = true;

        // get the search_context from the action. Could be directly attached to the action, or could
        // be inside of the action's payload.
        const searchContext: SearchContextValueType =
            action?.search_context || action?.payload?.search_context; // js-style action creators // actions using createAction
        if (searchContext !== option) {
            sendToReducer = false;
        }

        const actionHasFolderId = !!action?.folderId || !!action?.payload?.folderId;
        const actionIsInUploadedVisuals = searchContext === SEARCH_CONTEXT.UPLOADED_VISUALS;
        const actionIsInUploadedIcons = searchContext === SEARCH_CONTEXT.UPLOADED_ELEMENTS;

        if (actionHasFolderId && (actionIsInUploadedVisuals || actionIsInUploadedIcons)) {
            // folders live inside of 'uploaded-visuals' tab, so if user is inside a folder, actions would route to 2 reducers:
            // all_uploads_folders and uploaded-visuals. We want to set send_to_reducer to false if action contains folderId
            // since it'll be handled by all_uploads_folders.
            //
            // Same idea for 'uploaded-icons'.
            sendToReducer = false;
        }

        // the initialization event must be sent to the reducer so that the redux
        // state has the right shape
        if (state === undefined) {
            sendToReducer = true;
        }

        if (sendToReducer) {
            return reducer(state, action);
        } else {
            return state;
        }
    };
};

type ReducerMap = Record<SearchContextValueType, ReturnType<typeof reducer_override>>;
const reducer_map = {} as ReducerMap;

// make a reducer for each category of media that exists.
for (const option of Object.values(SEARCH_CONTEXT)) {
    reducer_map[option] = reducer_override(option);
}

export const media = combineReducers({
    search_context,
    all_favorite_media,
    playingAudioDetails,
    all_uploads_folders,
    active_uploads_folder,
    current_media_query_scene,
    is_media_query_search_loading,
    media_query_search_results,
    show_media_query_suggestion_error,
    is_similar_search_context,
    is_similar_search_loading,
    similar_search_results,
    similar_search_original_asset,
    ai_similar_generations,
    ai_similar_generations_loading,
    selected_reference_image,
    current_media_generations,
    previous_media_generations,
    is_fetching_recommended_search_terms,
    recommended_search_terms,
    media_errors,
    ai_media_models,
    video_generation_tasks,
    chart_generation_asset,
    is_initializing_video_generation,
    is_initializing_image_generation,
    active_ai_media_sub_tab,
    user_selected_gen_media_aspect_ratio,
    ...reducer_map,
});

// something gets messed up with the reducer_map type when we use combineReducers,
// so we add in the correct type for the reducer_map here:
export type MediaState = Omit<ReturnType<typeof media>, SearchContextValueType> &
    Record<SearchContextValueType, ReturnType<(typeof reducer_map)[SearchContextValueType]>>;

/*
 * We need to be able to undo/redo search term recommendations as users undo/redo article import
 */
export const undoable_media = combineReducers({
    is_fetching_recommended_search_terms,
    recommended_search_terms,
});
